import type { Environment, Project } from '@hangar/shared';
import { toast } from 'sonner';
import { useEnvironmentMutations } from '@/api/hooks';
import { showMutationError } from '@/lib/mutationError';
import { Icon } from '@/components/Icon';
import { EnvPill, Tag } from '@/components/badges';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { archivedSummary, environmentEtag } from './environmentsCardModel';

interface Props {
  project: Project;
  archived: Environment[];
  canRestore: boolean;
  hidden: boolean;
}

/** "Archived (n)": archived environments keep their slug; Restore brings one back (`archived: false`). */
export function ArchivedEnvironments({ project, archived, canRestore, hidden }: Props) {
  const { update } = useEnvironmentMutations(project.id);
  const restore = async (env: Environment) => {
    try {
      await update.mutateAsync({ environmentId: env.id, body: { archived: false }, etag: environmentEtag(env, project) });
      toast.success(`${env.slug} restored`);
    } catch (e) {
      showMutationError(e, 'Could not restore the environment');
    }
  };
  return (
    <Collapsible className="border-t">
      <CollapsibleTrigger asChild>
        <button type="button" data-testid="env-archived-toggle" className="group flex min-h-11 w-full items-center gap-2 px-4 text-left text-[13px] text-muted-foreground outline-none hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/50 sm:px-5">
          <Icon name="chevron_right" size={18} className="transition-transform duration-150 group-data-[state=open]:rotate-90" />
          Archived environments
          <Tag className="tabular">{archived.length}</Tag>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <ul className="flex flex-col gap-1 px-4 pb-4 sm:px-5" aria-label="Archived environments">
          {archived.map((env) => (
            <ArchivedItem key={env.id} env={env} hidden={hidden} disabled={!canRestore || update.isPending} onRestore={() => void restore(env)} />
          ))}
        </ul>
      </CollapsibleContent>
    </Collapsible>
  );
}

function ArchivedItem({ env, hidden, disabled, onRestore }: { env: Environment; hidden: boolean; disabled: boolean; onRestore: () => void }) {
  return (
    <li data-testid={`env-archived-${env.slug}`} className="flex min-w-0 flex-wrap items-center gap-2 rounded-lg border bg-card px-3 py-2 dark:bg-background">
      <EnvPill env={{ ...env, name: env.slug }} className="opacity-70" />
      <span className="min-w-0 flex-1 truncate font-mono text-xs text-subtle">{archivedSummary(env)}</span>
      {hidden ? null : (
        <Button size="sm" variant="ghost" data-testid="env-restore" disabled={disabled} onClick={onRestore}>
          Restore
        </Button>
      )}
    </li>
  );
}
